import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputAdornment,
  InputLabel,
  Checkbox,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { format, subMonths } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import { useAppSettings } from '../../context/SettingsContext';
import {
  fetchXeroInvoices,
  formatXeroDate,
  fmtXeroMoney,
  type XeroInvoiceFilter,
  type XeroInvoiceRow,
} from '../../services/xeroApi';
import { InvoiceDetailDialog } from '../../components/xero/InvoiceDetailDialog';
import { BatchReminderDialog } from '../../components/xero/BatchReminderDialog';
import { XeroHero, XeroStatusChip } from '../../components/xero/xeroShared';

const FILTERS: { value: XeroInvoiceFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'outstanding', label: 'Outstanding' },
  { value: 'paid', label: 'Paid' },
  { value: 'draft', label: 'Draft' },
];

const XeroInvoicesPage: React.FC = () => {
  const { settings } = useAppSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invoices, setInvoices] = useState<XeroInvoiceRow[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [remindOnOpen, setRemindOnOpen] = useState(false);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [batchOpen, setBatchOpen] = useState(false);

  const [filter, setFilter] = useState<XeroInvoiceFilter>(
    (searchParams.get('filter') as XeroInvoiceFilter) || 'unpaid'
  );
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [from, setFrom] = useState(searchParams.get('from') || format(subMonths(new Date(), 12), 'yyyy-MM-dd'));
  const [to, setTo] = useState(searchParams.get('to') || format(new Date(), 'yyyy-MM-dd'));
  const [minDue, setMinDue] = useState(searchParams.get('minDue') || '');
  const [maxDue, setMaxDue] = useState(searchParams.get('maxDue') || '');
  const contactId = searchParams.get('contactId') || '';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchXeroInvoices(settings, {
        filter,
        from,
        to,
        search: search.trim() || undefined,
        contactId: contactId || undefined,
        minDue: minDue ? Number(minDue) : undefined,
        maxDue: maxDue ? Number(maxDue) : undefined,
      });
      setInvoices(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [settings, filter, from, to, search, contactId, minDue, maxDue]);

  useEffect(() => {
    void load();
  }, [load]);

  const syncUrl = () => {
    const p = new URLSearchParams();
    if (filter !== 'unpaid') {
      p.set('filter', filter);
    }
    if (search) {
      p.set('search', search);
    }
    if (from) {
      p.set('from', from);
    }
    if (to) {
      p.set('to', to);
    }
    if (minDue) {
      p.set('minDue', minDue);
    }
    if (maxDue) {
      p.set('maxDue', maxDue);
    }
    setSearchParams(p, { replace: true });
  };

  const openInvoice = (id: string, remind = false) => {
    setSelectedId(id);
    setRemindOnOpen(remind);
    setDetailOpen(true);
  };

  const selectedRows = useMemo(
    () => invoices.filter((i) => selected[i.id] && i.amountDue > 0),
    [invoices, selected]
  );

  const toggleAll = (checked: boolean) => {
    if (!checked) {
      setSelected({});
      return;
    }
    const next: Record<string, boolean> = {};
    for (const inv of invoices.filter((i) => i.amountDue > 0)) {
      next[inv.id] = true;
    }
    setSelected(next);
  };

  const toggleRow = (id: string) => {
    setSelected((s) => ({ ...s, [id]: !s[id] }));
  };

  const batchSameCustomer = useMemo(() => {
    const contacts = new Set(selectedRows.map((i) => i.contactId || i.contact));
    return contacts.size <= 1;
  }, [selectedRows]);

  const openBatch = () => {
    if (!batchSameCustomer) {
      alert('Select invoices for the same customer to send one combined reminder.');
      return;
    }
    setBatchOpen(true);
  };

  const totals = useMemo(() => {
    const overdue = invoices.filter((i) => i.displayStatus === 'overdue');
    return {
      count: invoices.length,
      due: invoices.reduce((s, i) => s + i.amountDue, 0),
      overdue: overdue.reduce((s, i) => s + i.amountDue, 0),
      overdueCount: overdue.length,
    };
  }, [invoices]);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
        <XeroHero
          compact
          title="Xero · Invoices"
          subtitle="Filter unpaid and overdue invoices, view details, and send payment reminders."
        />
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => void load()} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={filter} onChange={(e) => setFilter(e.target.value as XeroInvoiceFilter)}>
                {FILTERS.map((f) => (
                  <MenuItem key={f.value} value={f.value}>
                    {f.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="From"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
            <TextField
              label="Search"
              size="small"
              placeholder="Invoice # or customer"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 200 }}
            />
            <TextField
              label="Min due"
              size="small"
              type="number"
              value={minDue}
              onChange={(e) => setMinDue(e.target.value)}
              sx={{ width: 110 }}
            />
            <TextField
              label="Max due"
              size="small"
              type="number"
              value={maxDue}
              onChange={(e) => setMaxDue(e.target.value)}
              sx={{ width: 110 }}
            />
            <Button
              variant="contained"
              onClick={() => {
                syncUrl();
                void load();
              }}
            >
              Apply filters
            </Button>
          </Stack>
          <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {totals.count} invoice(s) · {fmtXeroMoney(totals.due)} due
              {totals.overdueCount > 0 && (
                <Typography component="span" color="error.main" fontWeight={700}>
                  {' '}
                  · {totals.overdueCount} overdue ({fmtXeroMoney(totals.overdue)})
                </Typography>
              )}
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {selectedRows.length > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" variant="outlined" onClick={openBatch}>
              Send combined reminder ({selectedRows.length})
            </Button>
          }
        >
          {selectedRows.length} invoice(s) selected ·{' '}
          {fmtXeroMoney(selectedRows.reduce((s, i) => s + i.amountDue, 0))} due
          {!batchSameCustomer && selectedRows.length > 1 && (
            <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
              Selected invoices are for different customers — pick one customer to combine.
            </Typography>
          )}
        </Alert>
      )}

      <Card>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedRows.length > 0 && selectedRows.length < invoices.filter((i) => i.amountDue > 0).length}
                  checked={
                    invoices.filter((i) => i.amountDue > 0).length > 0 &&
                    selectedRows.length === invoices.filter((i) => i.amountDue > 0).length
                  }
                  onChange={(e) => toggleAll(e.target.checked)}
                  inputProps={{ 'aria-label': 'Select all unpaid invoices' }}
                />
              </TableCell>
              <TableCell>Invoice #</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Due</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Due</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="center">Reminders sent</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={10}>
                  <Typography color="text.secondary" py={4} textAlign="center">
                    No invoices match your filters.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {invoices.map((inv) => (
              <TableRow
                key={inv.id}
                hover
                selected={!!selected[inv.id]}
                sx={{ cursor: 'pointer' }}
                onClick={() => openInvoice(inv.id)}
              >
                <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                  {inv.amountDue > 0 ? (
                    <Checkbox checked={!!selected[inv.id]} onChange={() => toggleRow(inv.id)} />
                  ) : null}
                </TableCell>
                <TableCell>
                  <Typography fontWeight={600}>{inv.number}</Typography>
                </TableCell>
                <TableCell>{inv.contact}</TableCell>
                <TableCell>{formatXeroDate(inv.date)}</TableCell>
                <TableCell>
                  {formatXeroDate(inv.dueDate)}
                  {inv.daysOverdue > 0 && (
                    <Typography variant="caption" display="block" color="error.main">
                      {inv.daysOverdue}d overdue
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <XeroStatusChip row={inv} />
                </TableCell>
                <TableCell align="right">{fmtXeroMoney(inv.amountDue, inv.currency)}</TableCell>
                <TableCell align="right">{fmtXeroMoney(inv.total, inv.currency)}</TableCell>
                <TableCell align="center">
                  <Typography
                    variant="body2"
                    fontWeight={inv.remindersSent ? 700 : 400}
                    color={inv.remindersSent ? 'text.primary' : 'text.secondary'}
                  >
                    {inv.remindersSent ?? 0}
                  </Typography>
                </TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
                    <Button size="small" startIcon={<VisibilityOutlinedIcon />} onClick={() => openInvoice(inv.id)}>
                      View
                    </Button>
                    {inv.amountDue > 0 && (
                      <Button
                        size="small"
                        color="secondary"
                        startIcon={<EmailOutlinedIcon />}
                        onClick={() => openInvoice(inv.id, true)}
                      >
                        Remind
                      </Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <InvoiceDetailDialog
        invoiceId={selectedId}
        open={detailOpen}
        initialShowReminder={remindOnOpen}
        onClose={() => {
          setDetailOpen(false);
          setRemindOnOpen(false);
        }}
        onSent={() => void load()}
      />

      <BatchReminderDialog
        open={batchOpen}
        invoices={selectedRows}
        onClose={() => setBatchOpen(false)}
        onSent={() => {
          setBatchOpen(false);
          setSelected({});
          void load();
        }}
      />
    </Box>
  );
};

export default XeroInvoicesPage;
